import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useApi } from '../../common/hooks/useApi';
import { MpCardSection, MpCardSectionRef } from '../molecules/mp-card-section/mp-card-section';
import { Checkout } from '../../common/types/checkout';
import { UpSellIntent } from '../../common/types/up-sell-intent';
import { FormDataShape } from '../../common/hooks/useCheckoutController/useCheckoutController.types';
import { resolveUpsellOffer } from '../../common/utils/resolve-upsell-offer';

export const UpSellCardIframePage: React.FC = () => {
  const { upsellId } = useParams<{ upsellId: string }>();
  const [params] = useSearchParams();

  const vendaId = params.get('vendaId');
  const api = useApi();
  const cardRef = useRef<MpCardSectionRef>(null);

  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const [intent, setIntent] = useState<UpSellIntent | null>(null);
  const requestId = params.get('requestId') ?? '';

  // (se quiser) já puxa o cpf da intent
  const [data, setData] = useState<FormDataShape>({
    cpf: '',
    cardExpiry: '',
    cardCvv: '',
  } as any);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        if (!upsellId || !vendaId) {
          setError('Parâmetros inválidos.');
          return;
        }

        const { data: intent } = await api.UpSell.intent(upsellId, vendaId);

        setIntent(intent);

        const originalCheckout = intent?.venda?.checkout ?? null;
        const resolvedOffer = resolveUpsellOffer(intent?.upsell);
        const upsellAmount = resolvedOffer.amount;
        const upsellCurrency = resolvedOffer.currency;

        if (!originalCheckout) {
          setError('Checkout não encontrado.');
          return;
        }

        if (!upsellAmount || Number.isNaN(Number(upsellAmount))) {
          setError('Preço do upsell inválido.');
          return;
        }

        const checkoutForUpsell: Checkout = {
          ...originalCheckout,
          recorrencia: resolvedOffer.isRecurring,
          moeda: upsellCurrency ?? originalCheckout.moeda,
          subtotal: upsellAmount,
          total: upsellAmount,

          valor: upsellAmount,
          valorParcela1x: upsellAmount,
          plano: resolvedOffer.isRecurring
            ? ({
                ...originalCheckout.plano,
                ...intent?.upsell?.plano,
                billingConfig:
                  intent?.upsell?.resolvedBillingConfig ??
                  intent?.upsell?.plano?.billingConfig,
                trialConfig:
                  intent?.upsell?.resolvedTrialConfig ??
                  intent?.upsell?.plano?.trialConfig,
              } as any)
            : originalCheckout.plano,
        } as any;

        setCheckout(checkoutForUpsell);

        setData((d) => ({
          ...d,
          cpf: intent?.venda?.cpfComprador ?? '',
        }));
      } catch {
        setError('Não foi possível carregar os dados do pagamento.');
      }
    })();
  }, []);

  const onChange = (field: keyof FormDataShape, value: string) => {
    setData((d) => ({ ...d, [field]: value }));
  };

  const handleCancel = () => {
    window.parent.postMessage(
      { source: 'VendepayUpsell', type: 'CC_CLOSED', payload: { requestId } },
      '*',
    );
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      await cardRef.current?.createToken();
      const raw = cardRef.current?.getPayload();

      if (!raw) {
        setError('Dados do cartão inválidos.');
        return;
      }

      // ✅ normaliza SEM mexer no MpCardSection
      const token = raw.token ?? raw?.mp?.token;
      const installments = raw.installments ?? raw?.mp?.installments ?? 1;

      const paymentMethodId =
        raw.paymentMethodId ??
        raw.payment_method_id ??
        raw.payment_method?.id ??
        raw?.mp?.paymentMethodId ??
        raw?.mp?.payment_method_id ??
        raw?.mp?.payment_method?.id ??
        null;

      const issuer =
        raw.issuer ??
        raw.issuer_id ??
        raw?.mp?.issuer ??
        raw?.mp?.issuer_id ??
        null;

      if (!token) {
        setError('Token do cartão não gerado.');
        return;
      }

      window.parent.postMessage(
        {
          source: 'VendepayUpsell',
          type: 'CC_TOKEN',
          payload: {
            requestId,
            mp: { token, installments, paymentMethodId, issuer },
          },
        },
        '*',
      );
    } catch {
      setError('Não foi possível validar o cartão.');
    } finally {
      setLoading(false);
    }
  };

  if (error && !checkout) return <Center>{error}</Center>;

  if (!checkout) return <Center>Carregando...</Center>;

  return (
    <Wrapper>
      <MpCardSection
        ref={cardRef}
        checkout={checkout}
        data={data}
        onChange={onChange}
        payerEmail={intent?.venda?.emailComprador}
      />

      {error && <Error>{error}</Error>}

      <Actions>
        <Cancel onClick={handleCancel}>Cancelar</Cancel>
        <Confirm onClick={handleSubmit} disabled={loading}>
          {loading ? 'Processando...' : 'Concluir'}
        </Confirm>
      </Actions>
    </Wrapper>
  );
};


const Wrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const Confirm = styled.button`
  flex: 1;
  background: #10b981;
  color: #fff;
  border-radius: 10px;
  border: none;
  padding: 12px;
  font-weight: 700;
`;

const Cancel = styled.button`
  flex: 1;
  background: #f3f4f6;
  color: #111827;
  border-radius: 10px;
  border: none;
  padding: 12px;
`;

const Error = styled.div`
  color: #ef4444;
  font-size: 0.85rem;
  text-align: center;
`;

const Center = styled.div`
  padding: 40px;
  text-align: center;
`;
